import {Component, OnDestroy, OnInit} from '@angular/core';
import {NgForm} from '@angular/forms';
import {Location} from '@angular/common';
import {DispatcherService, ExternalReferenceService, LoaderService, ToastService} from 'front-base-angular-core/angular/projects/front-base-angular-core/src/public-api';
import {appointmentLinkVerificationCodes, appointmentWizardStepKeys} from '@constants/appointment.constants';
import {AppointmentRequest} from '@shared/models/appointment-request.model';
import {WizardStep} from '@shared/models/wizard.model';
import {LoginService} from '@shared/services/login.service';
import {ActivatedRoute} from '@angular/router';
import {Request} from '@shared/models/request.model';
import {AppConfigService} from '@shared/services/app-config.service';
import {configVariableValues} from '@shared/constants/config-variable-values.constants';
import {Appointment} from '@shared/models/appointment.model';
import * as moment from 'moment';
import {LinkPatientService} from '@shared/services/link-patient.service';
import {AppointmentService} from '@shared/services/appointment.service';
import {UserLink} from '@shared/models/user-profile.model';
import {LiveAnnouncer} from '@angular/cdk/a11y';
import {UserService} from '@shared/services/user.service';
import {PermissionService} from '@shared/services/permission.service';
import {LoginResendEmail} from '@shared/models/login.model';

@Component({
  selector: 'si-appo-appointment-request-page',
  templateUrl: './appo-appointment-request-page.component.html',
  styleUrls: ['./appo-appointment-request-page.component.scss']
})
export class AppoAppointmentRequestPageComponent implements OnInit, OnDestroy {
  observerKey = 'appoAppointmentRequestPage';
  stepKeys = appointmentWizardStepKeys;
  steps = [
    {
      key: this.stepKeys.REQUIREMENTS,
      name: 'SI_REQUIREMENTS'
    },
    {
      key: this.stepKeys.DATE_AND_TIME,
      name: 'SI_DATE_AND_TIME'
    },
    {
      key: this.stepKeys.IDENTIFICATION,
      name: 'SI_IDENTIFICATION',
      hideTitle: true
    },
    {
      key: this.stepKeys.CONFIRMATION,
      name: 'SI_APPOINTMENT_CONFIRMATION'
    }
  ] as WizardStep[];
  model = new AppointmentRequest();
  currentStep = 0;
  form: NgForm;
  appoAppointmentRequestRequirementsFormId = 'appoAppointmentRequestRequirementsForm';
  appoAppointmentRequestDateFormId = 'appoAppointmentRequestDateForm';
  appoAppointmentRequestConfirmFormId = 'appoAppointmentRequestConfirmForm';
  appoAppointmentRequestIdentificationFormId = 'appoAppointmentRequestIdentificationForm';
  finish: boolean;
  finishError: boolean;
  textError: string;
  slotsUpdate = 0;
  slotReservationDelete = 0;
  slotConfirmation = 0;
  displayLinkPatientPage: boolean;
  linkPatientVerification = 0;
  configVariableValues = configVariableValues;
  stepOneReadMode: boolean;
  hasSmsVerification: boolean;
  hasEmailVerification: boolean;
  displayExistingAccountDialog: boolean;
  passwordErrorText: string;
  captchaRendered: boolean;
  createIfNotLink = false;
  displayLogRegisterFailDialog = false;
  displayVerificationDialog = false;
  displayLogRegisterLinkQuestionDialog = false;
  displayResendVerifyEmailDialog = false;
  displayLogExistingAccountDialog = false;
  validationErrorTextIdentityDocument: string;
  registerLinkQuestionText: string;
  verificationErrorText: string;
  registerFailText: string;
  existingAccountText: string;
  isRegistered = false;
  isFinishClicked = false;
  collectiveWarningCheck = 0;

  constructor(
    private location: Location,
    private dispatcher: DispatcherService,
    private loginService: LoginService,
    private route: ActivatedRoute,
    private appConfigService: AppConfigService,
    private linkPatientService: LinkPatientService,
    private loaderService: LoaderService,
    private appointmentService: AppointmentService,
    private liveAnnouncer: LiveAnnouncer,
    private permissionService: PermissionService,
    private userService: UserService,
    private toastService: ToastService,
    private externalReferenceService: ExternalReferenceService
  ) {
  }

  ngOnInit(): void {
    this.registerBackIconDispatcher();
    this.manageWizardSteps();
    this.checkResolvers();
    this.getAppointmentRequestConfiguration();
  }

  registerBackIconDispatcher(): void {
    this.dispatcher.register('pasi-history-back-icon-clicked', this.onClickHistoryBackIcon.bind(this), this.observerKey);
  }

  manageWizardSteps(): void {
    if (this.loginService.isLoggedIn() || this.isRegistered) {
      this.steps = this.steps.filter(step => step.key !== this.stepKeys.IDENTIFICATION);
    }
  }

  async getAppointmentRequestConfiguration(): Promise<void> {
    await this.appConfigService.getConfigurations();
    this.hasSmsVerification = this.appConfigService.getVariableValue(configVariableValues.DOUBLE_FACTOR_AUTH_METHOD) === appointmentLinkVerificationCodes.SMS;
    this.hasEmailVerification = this.appConfigService.getVariableValue(configVariableValues.DOUBLE_FACTOR_AUTH_METHOD) === appointmentLinkVerificationCodes.EMAIL;
    this.setPreferredInsurance();
  }

  setPreferredInsurance(): void {
    const preferredCollectiveId = this.loginService.getPreferredCollectiveId();
    const privateCollectiveIdsList = this.appConfigService.getVariableValue(configVariableValues.DEFAULT_PRIVATE_PATIENT_INSURANCES)?.split(',');
    this.model.privateInsurance = !preferredCollectiveId || privateCollectiveIdsList.includes(preferredCollectiveId.toString());
    if (this.model.privateInsurance) {
      this.model.collective = null;
    }
  }

  checkResolvers(): void {
    const mtest = this.route.snapshot.data.request;
    const appointment = this.route.snapshot.data.appointment;
    if (!mtest && !appointment) {
      return;
    }

    mtest ? this.parseMedicalTest(mtest) : this.parseAppointment(appointment);
    this.stepOneReadMode = true;
    this.currentStep = 1;
  }

  parseMedicalTest(mtest: Request): void {
    this.model.proposalId = mtest.id;
    this.model.center = {
      id: mtest.destCenterId,
      name: mtest.destCenterName
    };
    this.model.service = {
      id: mtest.destServiceId,
      name: mtest.destServiceName
    };
    if (mtest.schedule?.id || mtest.professional?.id) {
      this.model.professional = {
        id: mtest.schedule?.id || mtest.professional?.id,
        name: mtest.schedule?.name || mtest.professional?.name
      };
    }

    this.model.appointmentReason = mtest.benefits;
    this.model.fromDate = Math.max(mtest.targetDate, Date.now());
    const privateCollectiveIdsList = this.appConfigService.getVariableValue(configVariableValues.DEFAULT_PRIVATE_PATIENT_INSURANCES).split(',');
    this.model.privateInsurance = privateCollectiveIdsList.includes(mtest.guarantorType?.collective?.id.toString());
    this.model.collective = {
      id: mtest.guarantorType?.collective?.id,
      name: `${mtest.guarantorType?.insurance?.name}/${mtest.guarantorType?.collective?.name}`
    };
  }

  parseAppointment(appointment: Appointment): void {
    this.model.oldAppointmentId = appointment.id;
    this.model.center = {
      id: appointment.centerId,
      name: appointment.centerName
    };
    this.model.service = {
      id: appointment.serviceId,
      name: appointment.serviceName
    };

    if (appointment.professionalId) {
      this.model.professional = {
        id: appointment.professionalId,
        name: appointment.professionalName
      };
    }

    this.model.appointmentReason = !appointment.benefits?.length ? [{
      id: appointment.reasonId,
      name: appointment.reasonName
    }] : appointment.benefits;
    this.model.fromDate = moment().set({second: 0, millisecond: 0}).valueOf();
    const privateCollectiveIdsList = this.appConfigService.getVariableValue(configVariableValues.DEFAULT_PRIVATE_PATIENT_INSURANCES).split(',');
    this.model.privateInsurance = privateCollectiveIdsList.includes(appointment.requestGuarantorType?.collective?.id.toString());
    this.model.collective = {
      id: appointment.requestGuarantorType?.collective?.id,
      name: `${appointment.requestGuarantorType?.insurance?.name}/${appointment.requestGuarantorType?.collective?.name}`
    };
  }

  onClickHistoryBackIcon(): void {
    if (this.displayLinkPatientPage) {
      this.displayLinkPatientPage = false;
    } else if (this.currentStep <= 0) {
      this.location.back();
    } else {
      this.currentStep -= 1;
    }
  }

  onClickPrevious(): void {
    switch (this.steps[this.currentStep].key) {
      case this.stepKeys.CONFIRMATION:
        this.slotReservationDelete += 1;
        break;
      default:
        this.currentStep <= 0
          ? this.currentStep = 0
          : this.currentStep -= 1;
    }
  }

  async onClickNext(): Promise<void> {
    switch (this.steps[this.currentStep].key) {
      case this.stepKeys.REQUIREMENTS:
        this.currentStep = 1;
        break;
      case this.stepKeys.DATE_AND_TIME:
        if (!this.loginService.isLoggedIn()) {
          this.currentStep = 2;
        } else if (!this.loginService.getIsPatient()) {
          this.manageMustBePatientAccount();
        } else if (!this.model.reserveId) {
          this.appointmentSlotReservation();
        } else {
          this.currentStep = 2;
        }

        break;
      case this.stepKeys.IDENTIFICATION:
        if (this.isRegistered) {
          this.manageWizardSteps();
        } else if (!this.loginService.isLoggedIn()) {
          await this.checkExistingAccount();
          await this.checkPassword();
          this.register();
        } else {
          this.manageWizardSteps();
          this.onClickNext();
        }

        break;
      case this.stepKeys.CONFIRMATION:
        if (!this.loginService.isLoggedIn()) {
          this.login();
        } else if (!this.loginService.getIsPatient()) {
          this.manageMustBePatientAccount();
        } else if (!this.model.reserveId) {
          this.appointmentSlotReservation();
        } else if (this.isFinishClicked) {
          this.slotConfirmation += 1;
        }

        break;
      default:
    }
  }

  onLoginSuccessChange(): void {
    this.collectiveWarningCheck += 1;
    this.onClickNext();
  }

  onClickContinue(): void {
    this.isFinishClicked = false;
    this.onClickNext();
  }

  onClickFinish(): void {
    this.isFinishClicked = true;
    this.onClickNext();
  }

  async manageMustBePatientAccount(): Promise<any> {
    try {
      this.loaderService.start();
      const userLink = {
        refreshToken: this.loginService.getSession()?.refresh_token
      } as UserLink;
      const patientHisValidation = await this.appointmentService.getCheckExistingPatientAndCreateIfDoesNot(userLink);
      if (patientHisValidation?.existHis) {
        this.linkPatientService.setShowLinkPatientWarning(true);
        if (this.hasSmsVerification || this.hasEmailVerification) {
          this.linkPatientVerification += 1;
        } else {
          this.displayExistingAccountDialog = true;
        }
      } else {
        this.loginService.afterLogin(patientHisValidation?.userSession);
        this.model.refreshUserRole = true;
        this.onFinishTransparentCreatePatient();
      }
    } catch (e) {
      if (e.error?.applicationCode === 'PCS0014') {
        setTimeout(() => {
          this.textError = e.error?.message;
          this.finishError = true;
        });
      }

      throw e;
    } finally {
      this.loaderService.stop();
    }
  }

  async appointmentSlotReservation(): Promise<void> {
    try {
      this.loaderService.start();
      const collective = this.model.collective; //Keep previous collective
      this.model = Object.assign(this.model, this.model.slotSelected.appointmentRequest);
      this.model.collective = collective; //Assign previous collective
      const reserveResponse = await this.appointmentService.setAppointmentSlotReserve(this.model);
      this.model.reserveId = reserveResponse.reserveId;
      this.onClickNext();
    } catch (e) {
      if (e.error?.applicationCode === 'PCS0013') {
        this.slotsUpdate += 1;
      } else { // On error in slot reservation, go back to date step
        this.currentStep = 1;
      }

      throw e;
    } finally {
      this.loaderService.stop();
    }
  }

  onUpdateExistingAccountDialogChange(): void {
    this.displayLinkPatientPage = true;
  }

  onFinishNhcHealthCardVerification(): void {
    this.collectiveWarningCheck += 1;
    this.onClickNext();
  }

  onFinishDoubleFactorVerification(): void {
    this.collectiveWarningCheck += 1;
    this.onClickNext();
  }

  onFinishTransparentCreatePatient(): void {
    this.collectiveWarningCheck += 1;
    this.onClickNext();
  }

  onChangeCaptchaRendered(captchaRendered: boolean): void {
    this.captchaRendered = captchaRendered;
  }

  async checkExistingAccount(): Promise<void> {
    this.validationErrorTextIdentityDocument = null;
    try {
      this.loaderService.start();
      await this.loginService.getCheckEmailByDocument(
        this.model.email,
        this.model.identityDocument?.value,
        this.model.identityDocument?.type?.code
      );
    } catch (e) {
      if (e.error?.applicationCode === 'PCS0006') {
        this.existingAccountText = e.error?.message;
        this.displayLogExistingAccountDialog = true;
        this.liveAnnouncer.announce(e.error.message, 'assertive');
      }

      if (e.error?.applicationCode === 'PCS0061') {
        this.validationErrorTextIdentityDocument = e.error?.message;
        this.liveAnnouncer.announce(e.error.message, 'assertive');
      }

      throw e;
    } finally {
      this.loaderService.stop();
    }
  }

  async checkPassword(): Promise<void> {
    try {
      this.loaderService.start();
      this.passwordErrorText = null;
      await this.loginService.passwordValidate(this.model.password);
    } catch (e) {
      if (e.error?.applicationCode === 'PCS0009') {
        this.passwordErrorText = e.error?.message;
        this.liveAnnouncer.announce(e.error.message, 'assertive');
      }

      throw e;
    } finally {
      this.loaderService.stop();
    }
  }

  async register(): Promise<void> {
    this.loaderService.start();
    try {
      await this.loginService.register(this.model, this.model.hasVisitedBefore === false || this.createIfNotLink);
      this.isRegistered = true;
      this.model.refreshUserRole = true;
      this.onClickNext();
    } catch (e) {
      if (e.error?.applicationCode === 'PCS0008') {
        this.displayLogRegisterFailDialog = true;
        this.registerFailText = e.error?.message;
        this.liveAnnouncer.announce(e.error.message, 'assertive');
      }

      if (e.error?.applicationCode === 'PCS0034') {
        this.displayVerificationDialog = true;
      }

      if (e.error?.applicationCode === 'PCS0035') {
        this.closeVerificationDialog();
        this.onChangeDisplayVerificationDialog(false);
      }

      if (e.error?.applicationCode === 'PCS0036') {
        this.verificationErrorText = e.error?.message;
        this.liveAnnouncer.announce(e.error.message, 'assertive');
      }

      if (e.error?.applicationCode === 'PCS0044') {
        this.displayLogRegisterLinkQuestionDialog = true;
        this.registerLinkQuestionText = e.error?.message;
        this.liveAnnouncer.announce(e.error.message, 'assertive');
      }

      if (!e.error?.applicationCode) {
        this.model.resetCaptcha += 1;
      }

      throw e;
    } finally {
      this.loaderService.stop();
    }
  }

  closeVerificationDialog(): void {
    this.displayVerificationDialog = false;
  }

  onChangeDisplayVerificationDialog(displayDialog: boolean): void {
    if (!displayDialog && !this.isRegistered) {
      this.model.verificationCode = null;
      this.verificationErrorText = null;
    }
  }

  onChangeVerificationCode(): void {
    this.register();
  }

  onClickEndRegistrationLinkQuestion(): void {
    this.createIfNotLink = true;
    this.register();
  }

  async login(): Promise<void> {
    try {
      this.loaderService.start();
      await this.loginService.login(this.model.email, this.model.password);
      await this.appConfigService.getConfigurations();
      await this.permissionService.getPermissions();
      this.userService.setInactivityFlag(false);
      this.model.refreshUserRole = true;
      this.onLoginSuccessChange();
    } catch (e) {
      if (e.error?.applicationCode === 'PCS0010') {
        this.displayResendVerifyEmailDialog = true;
      }

    } finally {
      this.loaderService.stop();
    }
  }

  async onClickButtonResendVerifyEmailDialog(action: boolean): Promise<void> {
    if (action) {
      await this.setResendVerifyEmail();
    }

    this.displayResendVerifyEmailDialog = false;
  }

  async setResendVerifyEmail(): Promise<void> {
    const resendEmailModel = {email: this.model.email} as LoginResendEmail;
    await this.loginService.setResendValidationEmail(resendEmailModel);
    this.toastService.success('SI_RESEND_VERIFY_EMAIL_SUCCESS');
  }

  ngOnDestroy(): void {
    this.dispatcher.unregisterFunction('pasi-history-back-icon-clicked', this.observerKey);
    if (this.model.refreshUserRole && !this.externalReferenceService.isActive()) {
      window.location.href = '/';
    }
  }
}
